import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faTimes,
  faEye,
  faDownload,
  faUpload,
  faPlus,
  faLink,
  faTrash,
  faPlay,
  faFilePdf,
  faImage,
  faVideo,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {
  getBibliothequeItems,
  createBibliothequeItem,
  updateBibliothequeItem,
  getBibliothequeCategories,
} from "../../../services/api";
import { BibliothequeItem, BibliothequeCategorie } from "../../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "../AdminLayout";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import { getFileName, isImageFile, resolveMediaUrl } from "../../../utils/media";

interface FileWithPreview extends File {
  preview?: string;
}

const BibliothequeForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [categories, setCategories] = useState<BibliothequeCategorie[]>([]);
  const [youtubeLinks, setYoutubeLinks] = useState<string[]>([]);
  const [newYoutubeLink, setNewYoutubeLink] = useState("");
  const [categoryType, setCategoryType] = useState<
    "images" | "videos" | "mixed" | null
  >(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<BibliothequeItem>();

  const selectedCategoryId = watch("categorieId");
  const etatValue = watch("etat");

  // Fetch YouTube video ID from URL
  const getYouTubeId = (url: string): string | null => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Validate YouTube URL
  const isValidYouTubeUrl = (url: string): boolean => {
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return youtubeRegex.test(url) && getYouTubeId(url) !== null;
  };

  // Add YouTube link
  const addYoutubeLink = () => {
    if (newYoutubeLink.trim() === "") return;

    if (!isValidYouTubeUrl(newYoutubeLink)) {
      toast.error("الرجاء إدخال رابط يوتيوب صحيح");
      return;
    }

    if (youtubeLinks.includes(newYoutubeLink)) {
      toast.error("هذا الرابط موجود بالفعل");
      return;
    }

    setYoutubeLinks([...youtubeLinks, newYoutubeLink]);
    setNewYoutubeLink("");
  };

  // Remove YouTube link
  const removeYoutubeLink = (index: number) => {
    setYoutubeLinks(youtubeLinks.filter((_, i) => i !== index));
  };

  // YouTube Embed Component
  const YouTubeEmbed = ({ videoId }: { videoId: string }) => {
    return (
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          src={`https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getBibliothequeCategories();
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("حدث خطأ أثناء جلب التصنيفات");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      const category = categories.find((c) => c.id === selectedCategoryId);
      if (category) {
        const arabicName = category.nomarabe.toLowerCase();
        const frenchName = category.nomfrancais.toLowerCase();

        // Determine category type based on name
        if (arabicName.includes("صور") || frenchName.includes("photos")) {
          setCategoryType("images");
        } else if (
          arabicName.includes("فيديوهات") ||
          frenchName.includes("vidéos")
        ) {
          setCategoryType("videos");
        } else if (
          arabicName.includes("لقاءات") ||
          frenchName.includes("rencontres")
        ) {
          setCategoryType("mixed");
        } else {
          setCategoryType("mixed"); // Default to mixed for unknown categories
        }
      }
    } else {
      setCategoryType(null);
    }
  }, [selectedCategoryId, categories]);

  useEffect(() => {
    if (isEditMode) {
      const fetchItem = async () => {
        try {
          setIsLoading(true);
          const response = await getBibliothequeItems();
          const item = response.data.find(
            (i: BibliothequeItem) => i.id === parseInt(id!)
          );
          if (item) {
            reset({
              ...item,
              etat: item.etat !== undefined ? item.etat : true,
            });
            if (item.fichiers) {
              setExistingFiles(item.fichiers);
            }
            if (item.liens) {
              setYoutubeLinks(item.liens);
            }
          }
        } catch (error) {
          console.error("Error fetching item:", error);
          toast.error("حدث خطأ أثناء جلب بيانات العنصر");
        } finally {
          setIsLoading(false);
        }
      };
      fetchItem();
    } else {
      setValue("etat", true);
      setValue("ordre", 0);
    }
  }, [id, isEditMode, reset, setValue]);

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const droppedFiles = Array.from(event.dataTransfer.files);

      // Filter files based on category type
      let validFiles = [];
      if (categoryType === "images") {
        validFiles = droppedFiles.filter((file) =>
          file.type.startsWith("image/")
        );
      } else if (categoryType === "videos") {
        // Videos category only accepts YouTube links, not file uploads
        toast.error(
          "تصنيف الفيديوهات لا يقبل رفع الملفات. يرجى إضافة روابط يوتيوب فقط."
        );
        setIsDragOver(false);
        return;
      } else {
        // Mixed category accepts both images and videos
        validFiles = droppedFiles.filter(
          (file) =>
            file.type.startsWith("image/") || file.type.startsWith("video/")
        );
      }

      if (validFiles.length > 0) {
        const newFiles = validFiles.map((file) => {
          const fileWithPreview = file as FileWithPreview;
          if (
            file.type.startsWith("image/") ||
            file.type.startsWith("video/")
          ) {
            fileWithPreview.preview = URL.createObjectURL(file);
          }
          return fileWithPreview;
        });

        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
      }
      setIsDragOver(false);
    },
    [categoryType]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);

      // Filter files based on category type
      let validFiles = [];
      if (categoryType === "images") {
        validFiles = selectedFiles.filter((file) =>
          file.type.startsWith("image/")
        );
      } else if (categoryType === "videos") {
        toast.error(
          "تصنيف الفيديوهات لا يقبل رفع الملفات. يرجى إضافة روابط يوتيوب فقط."
        );
        return;
      } else {
        validFiles = selectedFiles.filter(
          (file) =>
            file.type.startsWith("image/") || file.type.startsWith("video/")
        );
      }

      const newFiles = validFiles.map((file) => {
        const fileWithPreview = file as FileWithPreview;
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
          fileWithPreview.preview = URL.createObjectURL(file);
        }
        return fileWithPreview;
      });

      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const removeExistingFile = (index: number) => {
    setExistingFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const encodeFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const encodedContent = result;
        resolve(encodedContent);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data: BibliothequeItem) => {
    // Validation based on category type
    if (categoryType === "videos" && youtubeLinks.length === 0) {
      toast.error("تصنيف الفيديوهات يتطلب إضافة روابط يوتيوب على الأقل");
      return;
    }

    if (
      categoryType === "images" &&
      files.length === 0 &&
      existingFiles.length === 0
    ) {
      toast.error("تصنيف الصور يتطلب رفع صور على الأقل");
      return;
    }

    setIsLoading(true);
    try {
      // Convert new files to encoded payload strings
      const encodedFiles = await Promise.all(
        files.map((file) => encodeFileContent(file))
      );

      // Combine with existing files
      const allFiles = [
        ...existingFiles.map((file) => getFileName(file) || file),
        ...encodedFiles,
      ];

      const payload: any = {
        id: isEditMode ? parseInt(id!) : undefined,
        descriptionarabe: data.descriptionarabe,
        descriptionfrancais: data.descriptionfrancais,
        fichiers: allFiles,
        categorieId: data.categorieId,
        ordre: data.ordre,
        etat: data.etat,
      };

      // Only include links if it's a videos or mixed category
      if (categoryType === "videos" || categoryType === "mixed") {
        payload.liens = youtubeLinks;
      }

      if (!isEditMode) {
        delete payload.id;
      }

      if (isEditMode) {
        await updateBibliothequeItem(parseInt(id!), payload);
        toast.success("تم تحديث العنصر بنجاح");
      } else {
        await createBibliothequeItem(payload);
        toast.success("تمت إضافة العنصر بنجاح");
      }

      navigate("/admin/bibliotheque");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error(getTranslation("saveError", language));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  // Get category icon based on type
  const getCategoryIcon = () => {
    switch (categoryType) {
      case "images":
        return faImage;
      case "videos":
        return faVideo;
      case "mixed":
        return faUsers;
      default:
        return faPlus;
    }
  };

  // Get category label based on type
  const getCategoryLabel = () => {
    switch (categoryType) {
      case "images":
        return "تصنيف الصور (رفع صور فقط)";
      case "videos":
        return "تصنيف الفيديوهات (روابط يوتيوب فقط)";
      case "mixed":
        return "تصنيف اللقاءات (صور وفيديوهات)";
      default:
        return "اختر تصنيفاً";
    }
  };

  // Get category color based on type
  const getCategoryColor = () => {
    switch (categoryType) {
      case "images":
        return "bg-green-100 text-green-800";
      case "videos":
        return "bg-red-100 text-red-800";
      case "mixed":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark">
          {isEditMode ? "تعديل العنصر" : getTranslation("addLibraryItem", language)}
        </h1>
        <button
          onClick={() => navigate("/admin/bibliotheque")}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
          {getTranslation("back", language)}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Category Type Indicator */}
          {categoryType && (
            <div className="mb-6">
              <div
                className={`inline-flex items-center px-4 py-3 rounded-lg ${getCategoryColor()}`}
              >
                <FontAwesomeIcon
                  icon={getCategoryIcon()}
                  className="ml-3 text-xl"
                />
                <div>
                  <h4 className="font-bold">{getCategoryLabel()}</h4>
                  <p className="text-sm opacity-80 mt-1">
                    {categoryType === "images" && "يمكنك رفع صور متعددة فقط"}
                    {categoryType === "videos" &&
                      "يمكنك إضافة روابط يوتيوب متعددة فقط"}
                    {categoryType === "mixed" &&
                      "يمكنك رفع صور وإضافة روابط يوتيوب"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="descriptionarabe"
              >
                الوصف (عربي) *
              </label>
              <textarea
                id="descriptionarabe"
                rows={3}
                {...register("descriptionarabe", {
                  required: "هذا الحقل مطلوب",
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.descriptionarabe ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.descriptionarabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.descriptionarabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="descriptionfrancais"
              >
                الوصف (فرنسي) *
              </label>
              <textarea
                id="descriptionfrancais"
                rows={3}
                {...register("descriptionfrancais", {
                  required: "هذا الحقل مطلوب",
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.descriptionfrancais
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {errors.descriptionfrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.descriptionfrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Category, Order and Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="categorieId">
                التصنيف *
              </label>
              <select
                {...register("categorieId", {
                  required: "اختر تصنيفاً",
                  valueAsNumber: true,
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green"
              >
                <option value="">-- اختر تصنيفاً --</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nomarabe} - {cat.nomfrancais}
                  </option>
                ))}
              </select>
              {errors.categorieId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.categorieId.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2" htmlFor="ordre">
                الترتيب
              </label>
              <input
                id="ordre"
                type="number"
                {...register("ordre", { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2" htmlFor="etat">
                الحالة
              </label>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="etat"
                  {...register("etat")}
                  className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  checked={etatValue === undefined ? true : etatValue}
                  onChange={(e) => setValue("etat", e.target.checked)}
                />
                <label
                  htmlFor="etat"
                  className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                    etatValue === undefined || etatValue
                      ? "bg-mauritania-green"
                      : "bg-gray-300"
                  }`}
                ></label>
              </div>
              <span className="text-gray-700 font-medium">
                {etatValue === undefined || etatValue ? getTranslation("active", language) : "غير " + getTranslation("active", language)}
              </span>
            </div>
          </div>

          {/* YouTube Links Section (for videos and mixed categories) */}
          {(categoryType === "videos" || categoryType === "mixed") && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-gray-700 text-lg font-semibold">
                  روابط يوتيوب
                </label>
                <span className="text-sm text-gray-500">
                  {youtubeLinks.length} روابط مضافة
                </span>
              </div>

              <div className="space-y-4">
                {/* Add YouTube Link Form */}
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newYoutubeLink}
                    onChange={(e) => setNewYoutubeLink(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="flex-1 px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <button
                    type="button"
                    onClick={addYoutubeLink}
                    className="bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors flex items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="ml-2" />
                    إضافة رابط
                  </button>
                </div>

                {/* YouTube Links List */}
                {youtubeLinks.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center">
                      <FontAwesomeIcon
                        icon={faVideo}
                        className="ml-2 text-red-600"
                      />
                      روابط يوتيوب المضاف ({youtubeLinks.length})
                    </h4>

                    <div className="space-y-4">
                      {youtubeLinks.map((link, index) => {
                        const videoId = getYouTubeId(link);
                        return (
                          <div
                            key={index}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex flex-col lg:flex-row gap-4">
                              {/* YouTube Preview */}
                              {videoId && (
                                <div className="lg:w-1/3">
                                  <YouTubeEmbed videoId={videoId} />
                                </div>
                              )}

                              {/* Link Info */}
                              <div
                                className={`${videoId ? "lg:w-2/3" : "w-full"}`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-600 break-all font-medium">
                                      {link}
                                    </p>
                                    {videoId && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        معرّف الفيديو:{" "}
                                        <span className="font-mono">
                                          {videoId}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeYoutubeLink(index)}
                                    className="text-red-500 hover:text-red-700 ml-2 p-1"
                                    title="حذف الرابط"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                                <div className="flex gap-2 mt-3">
                                  <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded"
                                  >
                                    <FontAwesomeIcon
                                      icon={faLink}
                                      className="ml-1"
                                    />
                                    افتح في يوتيوب
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => window.open(link, "_blank")}
                                    className="inline-flex items-center text-sm bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded"
                                  >
                                    <FontAwesomeIcon
                                      icon={faPlay}
                                      className="ml-1"
                                    />
                                    شاهد الفيديو
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* File Upload Section (for images and mixed categories) */}
          {(categoryType === "images" || categoryType === "mixed") && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-gray-700 text-lg font-semibold">
                  {categoryType === "images" ? "الصور" : "الملفات المحملة"}
                </label>
                <span className="text-sm text-gray-500">
                  {files.length + existingFiles.length} ملفات
                </span>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isDragOver
                    ? "border-mauritania-green bg-mauritania-green-light"
                    : "border-gray-300 hover:border-mauritania-green"
                }`}
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => document.getElementById("file-input")?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept={
                    categoryType === "images" ? "image/*" : "image/*,video/*"
                  }
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {files.length > 0 || existingFiles.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {/* Existing files */}
                    {existingFiles.map((file, index) => (
                      <div key={`existing-${index}`} className="relative group">
                        <div className="h-32 w-full bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                          {isImageFile(file) ? (
                            <img
                              src={resolveMediaUrl(file)}
                              alt={`Existing ${index}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="text-center p-4">
                              <FontAwesomeIcon
                                icon={faVideo}
                                className="text-blue-500 text-2xl mb-2"
                              />
                              <p className="text-xs truncate">فيديو</p>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeExistingFile(index);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="حذف الملف"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </div>
                    ))}

                    {/* New files */}
                    {files.map((file, index) => (
                      <div key={`new-${index}`} className="relative group">
                        <div className="h-32 w-full bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                          {file.type.startsWith("image/") ? (
                            <img
                              src={file.preview}
                              alt={`Preview ${index}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="text-center p-4">
                              <FontAwesomeIcon
                                icon={faVideo}
                                className="text-blue-500 text-2xl mb-2"
                              />
                              <p className="text-xs truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </p>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="حذف الملف"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </div>
                    ))}

                    {/* Add more button */}
                    <div className="h-32 w-full border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center hover:border-mauritania-green transition-colors">
                      <FontAwesomeIcon
                        icon={faPlus}
                        className="text-gray-400 text-xl mb-2"
                      />
                      <p className="text-sm text-gray-600">إضافة المزيد</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                      <FontAwesomeIcon
                        icon={faUpload}
                        className="text-gray-400 text-2xl"
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      اسحب الملفات هنا أو انقر للاختيار
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {categoryType === "images"
                        ? "الصور (JPG, PNG, GIF)"
                        : "الصور (JPG, PNG, GIF), الفيديوهات (MP4, AVI, MOV)"}
                    </p>
                  </div>
                )}
              </div>

              {isEditMode && (
                <p className="text-sm text-gray-500 mt-2">
                  سيتم استبدال الملفات الحالية بالملفات الجديدة التي تقوم برفعها
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-mauritania-green text-white py-3 px-8 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50 flex items-center"
            >
              <FontAwesomeIcon icon={faSave} className="ml-2" />
              {isLoading ? getTranslation("saving", language) : getTranslation("save", language)}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default BibliothequeForm;
